import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDialogStateManager, useSiteModalOpen, useStandardApiRequest } from 'my-core/hooks';
import { useOnlineCourseContext, useOnlineCourseTokens } from 'my-core/in-course-experience-hooks';
import { useOnlineCourseProgressManager } from 'my-core/in-course-experience-progress-tracker';

import OnlineCoursePurchaseForm from 'my-components/OnlineCoursePurchaseForm';

import StudentCoursePageActivity from './StudentCoursePageActivity';

import { toggleLoginForm } from 'my-actions/AppStateActions';
import { createOnlineCourseUser } from 'my-actions/OnlineCourseUserActions';

export default function StudentCoursePageActivityCtnr({
  activeComponent,
  activityTitle,
  assessment,
  classPassId,
  course,
  currentUser,
  drawerOpen,
  fetchStatus,
  onToggleDrawer,
  pathMatchData,
  studySessions,
}) {
  // reselecting onlineCourse from store because on passed down depends on state.onlineCourseUsers which
  // changes often, causing memoized values to update (eg. eventTrackingProperties)
  const classPass = useSelector(state => state.onlineCourses.items[classPassId]);
  const { canViewAll, courseSubscription, onlineCourseUser, subscription, userCourse } = useOnlineCourseContext(
    currentUser,
    classPass,
    course,
  );

  const { purchaseWithToken, tokenPurchases, tokenPurchaseStatus } = useOnlineCourseTokens(currentUser.id, classPassId);

  const { enqueueProgressSave, getProgress } = useOnlineCourseProgressManager(onlineCourseUser);

  // CREATE ONLINE COURSE USER
  const { performRequest: performCreateOCU /*, requestStatus: createOCUStatus */ } = useStandardApiRequest({
    actionCreator: createOnlineCourseUser,
  });
  useEffect(() => {
    if (!fetchStatus?.success || currentUser.guest || onlineCourseUser?.id) return;
    performCreateOCU(classPassId);
  }, [currentUser.guest, fetchStatus?.success, classPassId, onlineCourseUser?.id, performCreateOCU]);

  const siteModalOpen = useSiteModalOpen();

  // ONLINE COURSE PURCHASE
  const dispatch = useDispatch();
  const [purchaseDialogOpen, hasPurchaseDialogOpened, purchaseDialogActions] = useDialogStateManager();
  const showPurchaseForm = useCallback(() => {
    if (currentUser.guest) {
      dispatch(
        toggleLoginForm({
          actionText: 'Watch Video Lessons',
          source: 'in_course_purchase',
        }),
      );
    } else {
      purchaseDialogActions.open(true);
    }
  }, [currentUser.guest, dispatch, purchaseDialogActions]);

  return (
    <>
      <StudentCoursePageActivity
        activeComponent={activeComponent}
        activityTitle={activityTitle}
        assessment={assessment}
        basePath={`../${pathMatchData.pathname}`}
        canViewAll={canViewAll}
        course={course}
        courseSubscription={courseSubscription}
        disabled={siteModalOpen}
        drawerOpen={drawerOpen}
        onlineCourse={classPass}
        onlineCourseUser={onlineCourseUser}
        onPurchase={showPurchaseForm}
        onSaveProgress={enqueueProgressSave}
        onToggleDrawer={onToggleDrawer}
        onTokenPurchase={purchaseWithToken}
        progress={activeComponent?.type === 'activity' ? getProgress(activeComponent.data.id) : undefined}
        studySessions={studySessions}
        subscription={subscription}
        tokenBalance={currentUser.token_balance || 0}
        tokenPurchases={tokenPurchases}
        tokenPurchaseStatus={tokenPurchaseStatus}
        userCourse={userCourse}
        // onShowFeedback={onShowFeedback}
        // onToggleAutoplay={toggleAutoplay}
        // onToggleReviewMode={toggleReviewMode}
        // reviewMode={reviewMode}
        // stripeProduct={stripeProduct}
        // autoplay={autoplay}
        // nextStudySession={nextStudySession}
        // onlineCourseContent={onlineCourseContent}
        // onStartTrial={onStartTrial}
        // subscriptionTrialStatus={subscriptionTrialStatus}
      />
      {hasPurchaseDialogOpened && (
        <OnlineCoursePurchaseForm
          onClose={purchaseDialogActions.close}
          onlineCourseId={classPassId}
          open={purchaseDialogOpen}
        />
      )}
    </>
  );
}
